/**
 * The last thing we knew, and when we knew it.
 *
 * A phone at the office gate is the worst network in the worker's day: inside a building, thirty
 * people on one cell, and the app opened cold from the home screen. The service worker gets our
 * shell onto the screen, but the shell on its own knows nothing — so until this existed, a worker
 * who was perfectly signed in was shown a sign-in page, which reads as "you have been logged out"
 * and sends them to the admin for a new invite they do not need.
 *
 * So we keep the last successful answer for the handful of things a worker might want to see
 * without a connection, each stamped with the time it was true. Nothing here is authority: the
 * session is still the httpOnly cookie, and no punch is ever made from cached state.
 */

const PREFIX = 'siva.last.';

export type Remembered<T> = { at: string; value: T };

/** Keep the latest successful answer. Failures are silent: private mode has no storage. */
export function remember<T>(key: string, value: T): void {
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify({ at: new Date().toISOString(), value }));
  } catch {
    /* private mode, or the quota is full: the app simply has nothing to fall back on */
  }
}

/** The last answer and its age, or null when there is none (or it is unreadable). */
export function recall<T>(key: string): Remembered<T> | null {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Remembered<T>;
    if (!parsed || typeof parsed.at !== 'string') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function forget(key: string): void {
  try {
    localStorage.removeItem(PREFIX + key);
  } catch {
    /* nothing to do */
  }
}

/** Everything, on sign-out: the next person to hold this phone is not this worker. */
export function forgetAll(): void {
  try {
    for (const k of Object.keys(localStorage)) {
      if (k.startsWith(PREFIX)) localStorage.removeItem(k);
    }
  } catch {
    /* nothing to do */
  }
}

export const LAST_ME = 'me';
export const LAST_PUNCH_STATUS = 'punchStatus';
export const LAST_HOURS = 'hours';
export const LAST_OFF_DAYS = 'offDays';
